import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "../modal/Modal";
import { CloseButton } from "../input/CloseButton";
import { TextInputField } from "../input/TextInputField";
import { useForm } from "react-hook-form";
import { Button } from "../input/Button";
import { Column } from "../layout/Column";
import { IconButton } from "../input/IconButton";
import { ReactComponent as AttachIcon } from "../icons/Attach.svg";
import { ReactComponent as CaretDownIcon } from "../icons/CaretDown.svg";
import { ReactComponent as CaretUpIcon } from "../icons/CaretUp.svg";
import { ReactComponent as TrashIcon } from "../icons/trashbin.svg";

import styles from "./ObjectUrlModal.scss";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";
import { ToggleInput } from "../input/ToggleInput";
import MyDropzone from "./myDropzone";

import ThumbnailRenderer from "../../utils/thumbnail-renderer";
import { generateImageFileThumbnail, generateVideoFileThumbnail } from "../../utils/thumbnails";
import { generatePDFThumbnail } from "../../utils/pdf-render";
import { CheckboxInput } from "../input/CheckboxInput";
import { ReactComponent as Check } from "../icons/Check2.svg";
import { ToggleInputUpload } from "../input/ToggleInputUpload";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

export function ObjectUrlUploadModal({
  showModelCollectionLink,
  modelCollectionUrl,
  onSubmit,
  onClose,
  sourceNavigate,
  errorUrl,
  onCleanUrl
}) {
  const { handleSubmit, register, watch, setValue } = useForm();
  const [files, setFiles] = useState([]);
  const [thumbnail, setThumbnail] = useState("");

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const processThumbnail = useCallback(
    async entry => {
      const thumbnailRenderer = new ThumbnailRenderer();

      let blob;
      if (entry?.name?.toLowerCase().endsWith(".glb")) {
        const loader = new GLTFLoader();
        const url = URL.createObjectURL(entry);

        const { scene } = await loader.loadAsync(url);

        URL.revokeObjectURL(url);

        blob = await thumbnailRenderer.generateThumbnail(scene);
      } else if ([".png", ".jpg", ".jpeg", ".gif", ".webp"].some(ext => entry?.name?.toLowerCase().endsWith(ext))) {
        blob = await generateImageFileThumbnail(entry);
      } else if (entry?.name?.toLowerCase().endsWith(".mp4")) {
        blob = await generateVideoFileThumbnail(entry);
      } else if (entry?.name?.toLowerCase().endsWith(".pdf") || entry?.url?.toLowerCase().endsWith(".pdf")) {
        blob = await generatePDFThumbnail(entry);
      }
      let urlFile = URL.createObjectURL(blob);
      setThumbnail(urlFile);

      const thumbnailFile = new File([blob], `${entry?.name.split(".").shift()}-thumb.png`, { type: "image/png" });

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(thumbnailFile);
      setValue("thumbnail", dataTransfer.files);
    },
    [setValue]
  );

  useEffect(() => {
    register("url");
    register("href");
    register("hideHref");
    register("smoothTransition");
    register("file");
    register("thumbnail");
    register("myMedia");
  }, [register]);

  const file = watch("file");
  const hasFile = file && file.length > 0;
  const fileName = hasFile ? file[0].name : undefined;
  const myMediaCheck = watch("myMedia");

  const onClear = useCallback(() => {
    if (hasFile) {
      setValue("file", undefined);
    } else {
      setValue("url", "");
    }
    setValue("thumbnail", undefined);
    setThumbnail("");
  }, [hasFile, setValue]);

  const handleFileLoaded = async acceptedFiles => {

    acceptedFiles[0].downloableUrl = acceptedFiles.downloableUrl;
    setValue("file", acceptedFiles);

    const acceptedFile = acceptedFiles[0];

    await processThumbnail(acceptedFile);
  };

  const onChange = useCallback(
    (e, field, type = "text") => {
      if (field === "url" && hasFile) {
        return;
      }

      setValue(field, type === "toggle" ? e.target.checked : e.target.value);
    },
    [hasFile, setValue]
  );

  const url = watch("url", "");
  const href = watch("href", "");
  const hideHref = watch("hideHref", false);
  const smoothTransition = watch("smoothTransition", false);

  useEffect(() => {
    if (url === "" || myMediaCheck === false) {
      onCleanUrl();
    }
  }, [url, myMediaCheck]);

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  const disableButton = hasFile || url.length > 0;

  return (
    <Modal
      id="upload-object-modal"
      title={"Upload File"}
      beforeTitle={<CloseButton onClick={onClose} />}
      className={styles.modal}
    >
      <Column as="form" padding center onSubmit={handleSubmit(onSubmit)}>
        <p style={{ maxWidth: "350px" }}>
          {showModelCollectionLink ? (
            <FormattedMessage
              id="object-url-modal.message-with-collection"
              defaultMessage="Upload or paste a URL to an image, video, model, or scene. You can also find assets in <medialink>Assets</medialink> or <medialink>External Assets</medialink>"
              values={{
                // eslint-disable-next-line react/display-name
                medialink: chunks => (
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => {
                      e.preventDefault();
                      if(window.showMyMedia) {                
                        window.showMyMedia();
                      }
                      onClose();
                    }}
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="object-url-modal.message"
              defaultMessage="Upload or paste a URL to an image, video, model, or scene. You can also find assets in <medialink>Assets</medialink> or <medialink>External Assets</medialink>"
              values={{
                // eslint-disable-next-line react/display-name
                medialink: chunks => (
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => {
                      e.preventDefault();
                      if(window.showMyMedia) {                
                        window.showMyMedia();
                      }
                      onClose();
                    }}
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          )}
        </p>

        <p className="extensions">Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files.</p>

        {!thumbnail && (
          <MyDropzone thumbnail={thumbnail} register={register} onFileLoaded={handleFileLoaded}></MyDropzone>
        )}
        {thumbnail && <ThumbnailComponent thumbnail={thumbnail} onClear={onClear} />}

        <input type="file" name="file" style={{ display: "none" }} />
        <input type="file" name="thumbnail" ref={register} style={{ display: "none" }} />

        <p className="or">OR</p>

        <TextInputField
          name="url"
          placeholder="Add Url..."
          type={hasFile ? "text" : "url"}
          value={fileName || url || ""}
          onChange={e => onChange(e, "url")}
          className="input"
        />

        <p className={styles.advancedOptionsButton} onClick={toggleAdvancedOptions}>
          <FormattedMessage id="room-settings-sidebar.advanced-options" defaultMessage="Advanced options" />
          {showAdvancedOptions ? <CaretUpIcon /> : <CaretDownIcon />}
        </p>

        {showAdvancedOptions && (
          <>
            <TextInputField
              className={classNames(styles.hrefInput, "input", "input-external-url")}
              name="href"
              placeholder="Add Url..."
              label={
                <FormattedMessage id="object-url-modal.href-field-label" defaultMessage="Attach custom external URL" />
              }
              description={
                <FormattedMessage
                  id="object-url-modal.href-field-description"
                  defaultMessage="Specify a custom URL for this object to an external site"
                />
              }
              type="text"
              value={href}
              onChange={e => onChange(e, "href")}
            />
            <ToggleInputUpload
              className={classNames(styles.hideHrefToggleInput, "input-toggle")}
              name="hideHref"
              label={
                <FormattedMessage
                  id="object-url-modal.hide-href-field-label"
                  defaultMessage="Hide 'Open Link' button"
                />
              }
              description={
                <FormattedMessage
                  id="object-url-modal.hide-href-field-description"
                  defaultMessage="Hide 'Open Link' button for the Custom URL and make the object fully clickable"
                />
              }
              checked={hideHref}
              onChange={e => onChange(e, "hideHref", "toggle")}
            />
          </>
        )}

        <Button
          type="submit"
          preset="accept"
          className={classNames("place-button", !hasFile && !url.length > 0 ? "disabled" : "")}
        >
          <FormattedMessage id="object-url-modal.create-object-button" defaultMessage="Place" />
        </Button>

        {errorUrl && <span className="error">Invalid URL, it should be an image, video, audio, pdf or GLB model</span>}
      </Column>
    </Modal>
  );
}

ObjectUrlUploadModal.propTypes = {
  isMobile: PropTypes.bool,
  showModelCollectionLink: PropTypes.bool,
  modelCollectionUrl: PropTypes.string,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func
};

function ThumbnailComponent({ thumbnail, onClear }) {
  return (
    <div className={styles.thumbnail}>
      <div className="remove" onClick={onClear}>
        <TrashIcon size={24} />
      </div>
      <img src={thumbnail}></img>
    </div>
  );
}
